import {
  IntegrationKey,
  FamousIntegrationSettings,
  PlaidIntegrationSettings,
  PaymentsIntegrationSettings,
  OracleFusionIntegrationSettings,
  JiraIntegrationSettings,
  MicrosoftTeamsIntegrationSettings,
  SlackIntegrationSettings,
} from '@erp/rpc';
import {
  StorefrontIcon,
  PackageIcon,
  LightningIcon,
  BankIcon,
  CreditCardIcon,
  CloudIcon,
  KanbanIcon,
  ChatCircleDotsIcon,
  PhoneIcon,
  SlackLogoIcon,
  Icon,
} from '@phosphor-icons/react';

export interface IntegrationConfig {
  key: IntegrationKey;
  name: string;
  description: string;
  longDescription: string;
  icon: Icon;
  category:
    | 'erp'
    | 'inventory'
    | 'accounting'
    | 'shipping'
    | 'notifications'
    | 'other';
  features: string[];
  isPopular?: boolean;
  defaultSettings?: unknown; // Default settings for when connecting the integration
  // Hide the integration entirely (instead of showing an "Unavailable" card)
  // when the backend reports it as unavailable. Used for env-gated
  // integrations that shouldn't advertise themselves when unconfigured.
  hideWhenUnavailable?: boolean;
}

export const INTEGRATIONS_CONFIG = {
  [IntegrationKey.Famous]: {
    key: IntegrationKey.Famous,
    name: 'FAMOUS™',
    description: 'ERP integration for fresh produce distributors',
    longDescription:
      'FAMOUS™ is a comprehensive ERP solution designed specifically for fresh produce distributors. This integration enables seamless synchronization of products, customers, inventory, and transactions from FAMOUS™',
    icon: StorefrontIcon,
    category: 'erp',
    features: [
      'Product catalog synchronization',
      'Customer data management',
      'Real-time inventory updates',
      'Custom product mapping',
      'Automated data synchronization',
      'FAPI translation support',
    ],
    isPopular: false,
    defaultSettings: {
      productMapping: {
        useCustomerProducts: true,
      },
      salesOrders: {
        removeZeroQuantityLineItemsOnApproval: true,
      },
    } satisfies FamousIntegrationSettings,
  },
  [IntegrationKey.Plaid]: {
    key: IntegrationKey.Plaid,
    name: 'Plaid',
    description:
      'Connect bank accounts for transaction sync and reconciliation',
    longDescription:
      'Plaid enables secure bank account connections for transaction history, balance visibility, and reconciliation workflows. Connect multiple bank accounts to streamline your financial operations.',
    icon: BankIcon,
    category: 'accounting',
    features: [
      'Secure bank account authentication',
      'Pull transaction history',
      'Check account balances',
      'Support for multiple bank connections',
      'Bank-level security and encryption',
    ],
    isPopular: false,
    defaultSettings: {
      accounts: [],
      cashReceiptMatchingConfig: { dateTolerance: 3 },
    } satisfies PlaidIntegrationSettings,
  },
  [IntegrationKey.Payments]: {
    key: IntegrationKey.Payments,
    name: 'Payments',
    description: 'ACH payments via Checkbook.io',
    longDescription:
      'Enable ACH payments to vendors through Checkbook.io. Add bank accounts directly, store banking details securely, and verify each funding account through micro-deposits before sending payments.',
    icon: CreditCardIcon,
    category: 'accounting',
    features: [
      'ACH payments to vendors',
      'Micro-deposit bank verification',
      'Secure payment processing',
      'Payment status tracking',
      'Multiple payment accounts',
    ],
    isPopular: false,
    defaultSettings: {
      accounts: [],
    } satisfies PaymentsIntegrationSettings,
  },
  [IntegrationKey.OracleFusion]: {
    key: IntegrationKey.OracleFusion,
    name: 'Oracle Fusion',
    description: 'Cloud ERP integration for product catalog sync',
    longDescription:
      'Oracle Fusion Cloud ERP integration enables synchronization of product catalogs from Oracle Fusion. Supports delta sync for efficient incremental updates and scheduled synchronization.',
    icon: CloudIcon,
    category: 'erp',
    features: [
      'Product catalog synchronization',
      'Delta sync for incremental updates',
      'Scheduled synchronization',
    ],
    isPopular: false,
    defaultSettings: {
      baseUrl: '',
      username: '',
      password: '',
    } satisfies OracleFusionIntegrationSettings,
  },
  [IntegrationKey.Jira]: {
    key: IntegrationKey.Jira,
    name: 'Jira',
    description: 'Create Jira issues from workflows to notify assignees',
    longDescription:
      'Connect Jira Cloud so workflows can file issues for notifications and follow-ups. The system resolves a recipient to their Jira account by email and creates an issue in your linked project, assigned to them.',
    icon: KanbanIcon,
    category: 'notifications',
    features: [
      'Create issues from workflow steps',
      'Auto-resolve assignee from internal user email',
      'Use a service account API token',
      'Single linked default project',
    ],
    isPopular: false,
    defaultSettings: {
      baseUrl: '',
      serviceAccountEmail: '',
      apiToken: '',
      defaultProjectKey: '',
    } satisfies JiraIntegrationSettings,
  },
  [IntegrationKey.MicrosoftTeams]: {
    key: IntegrationKey.MicrosoftTeams,
    name: 'Microsoft Teams',
    description: 'Send activity-feed notifications to users in Microsoft Teams',
    longDescription:
      'Connect Microsoft Teams so workflows can ping users in their Teams activity feed. One-click admin consent authorizes the Gently Teams app inside your tenant — no per-user setup. Designed to grow into chat and slash-command support later.',
    icon: ChatCircleDotsIcon,
    category: 'notifications',
    features: [
      'One-click admin consent (multi-tenant Azure AD app)',
      'Send activity-feed notifications from workflow steps',
      'Resolve recipients from internal user email',
      'Built on Microsoft Graph — extensible to chat and slash commands',
    ],
    isPopular: false,
    defaultSettings: {
      defaultActivityType: 'workflowNotification',
    } satisfies MicrosoftTeamsIntegrationSettings,
  },
  [IntegrationKey.Slack]: {
    key: IntegrationKey.Slack,
    name: 'Slack',
    description: 'Receive Slack messages, button clicks, and slash commands',
    longDescription:
      'Connect Slack so event-triggered workflows can react to inbound Slack activity — direct messages to your Slack app, interactive button clicks, and slash commands. Powers bots like the daily standup assistant. The Slack app credentials are configured server-side; installing this integration routes inbound Slack webhooks to your organization.',
    icon: SlackLogoIcon,
    category: 'notifications',
    features: [
      'Trigger workflows from Slack events (DMs, mentions)',
      'Handle interactive button clicks and modals',
      'Handle slash commands (e.g. /register)',
      'Optionally pin to a specific Slack workspace',
    ],
    isPopular: false,
    defaultSettings: {} satisfies SlackIntegrationSettings,
    hideWhenUnavailable: true,
  },
  [IntegrationKey.VoiceAgent]: {
    key: IntegrationKey.VoiceAgent,
    name: 'Voice Agent',
    description: 'AI-powered outbound voice calls in workflows',
    longDescription:
      'Configure an AI voice agent for making outbound phone calls as part of automated workflows. Requires an assistant and phone number configured in your voice provider dashboard.',
    icon: PhoneIcon,
    category: 'other',
    features: [
      'Outbound AI voice calls',
      'Call transcript and recording',
      'Post-call AI analysis',
      'Structured data extraction',
    ],
    isPopular: false,
    defaultSettings: undefined,
  },
} as const satisfies Record<IntegrationKey, IntegrationConfig>;

export const INTEGRATION_CATEGORIES = {
  erp: { name: 'ERP Systems', icon: StorefrontIcon },
  inventory: { name: 'Inventory Management', icon: PackageIcon },
  accounting: { name: 'Accounting', icon: LightningIcon },
  shipping: { name: 'Shipping & Logistics', icon: PackageIcon },
  notifications: { name: 'Notifications', icon: KanbanIcon },
  other: { name: 'Other', icon: LightningIcon },
} as const;
export function getIntegrationConfig<T extends IntegrationKey>(
  key: T,
): (typeof INTEGRATIONS_CONFIG)[T] {
  return INTEGRATIONS_CONFIG[key];
}

export function getAllIntegrationConfigs(): IntegrationConfig[] {
  return Object.values(INTEGRATIONS_CONFIG);
}
